/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import { getLocalizedString } from '../../util/getLocalizedString';
import type { Question } from '../../types/types';

const getQuestionTypeLabel = (type: string) => {
  switch (type) {
    case 'text':
      return <span>Users will enter a short text answer.</span>;
    case 'multiline':
      return <span>Users will enter a longer text answer.</span>;
    case 'number':
      return <span>Users will enter a numeric answer.</span>;
    case 'percentage':
      return (
        <span>Users will enter a percentage (0-100) with a 0.5% step</span>
      );
    case 'single_choice':
      return <span></span>;
    case 'multi_choice':
      return <span></span>;
    case 'boolean':
      return <span>Users will answer Yes or No.</span>;
    case 'date':
      return <span>Users will select a date.</span>;
    case 'file':
      return <span>Users will upload a file.</span>;
    case 'country':
      return <span>Users will select a country from a list.</span>;
    case 'industry':
      return (
        <span>
          Users will select an industry from a list of NACE (Eurostat standard)
          industry.
        </span>
      );
    default:
      return <span>Unknown question type.</span>;
  }
};

const QuestionForm = ({ draft, setDraft, section }: any) => {
  const handleChange = (field: string, value: any) => {
    setDraft((prev: Question) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Label</Form.Label>
        <Form.Control
          type="text"
          value={getLocalizedString(draft.label) ?? ''}
          onChange={(e) => handleChange('label', e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Help Text</Form.Label>
        <Form.Control
          type="text"
          value={getLocalizedString(draft.help_text) ?? ''}
          onChange={(e) => handleChange('help_text', e.target.value)}
        />
      </Form.Group>

      <Row className="mb-2">
        <Col>
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={draft.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              <option value="text">Text</option>
              <option value="multiline">Long Text</option>
              <option value="number">Number</option>
              <option value="percentage">Percentage</option>
              <option value="single_choice">Single Choice</option>
              <option value="multi_choice">Multi Choice</option>
              <option value="boolean">Yes/No</option>
              <option value="date">Date</option>
              <option value="file">File</option>
              <option value="country">Country</option>
              <option value="industry">Industry</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            label="Required"
            checked={draft.required}
            id="required-checkbox"
            onChange={(e) => handleChange('required', e.target.checked)}
          />
          <Form.Check
            type="checkbox"
            label="Hidden"
            className="ms-3"
            checked={draft.hidden}
            id="hidden-checkbox"
            onChange={(e) => handleChange('hidden', e.target.checked)}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col>{getQuestionTypeLabel(draft.type)}</Col>
      </Row>
      {draft.hidden && (
        <Row className="mb-2">
          <Col>
            <Form.Group className="mb-2 rounded p-3 border border-secondary">
              <Form.Label>
                This question will be shown only when the following question is
                answered positively (YES):
              </Form.Label>
              <Form.Select
                defaultValue={draft.condition?.depends_on ?? ''}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const depQuestion = (section.questions || []).find(
                    (q: Question) => q.id === selectedId
                  );
                  handleChange('condition', {
                    value: true,
                    depends_on: (depQuestion as Question)?.id ?? selectedId,
                  });
                }}
              >
                <option value="" disabled>
                  Select a question in this section
                </option>
                {(section.questions || [])
                  .filter((q: Question) => q.id !== draft.id)
                  .map((q: Question) => (
                    <option key={q.id} value={q.id}>
                      {typeof q.label === 'string' && (q.label as string).trim()
                        ? (q.label as string)
                        : 'Untitled question'}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          {draft.type === 'single_choice' && (
            <span>Users will select one option:</span>
          )}
          {draft.type === 'multi_choice' && (
            <span>Users can select multiple options:</span>
          )}
        </Col>
      </Row>
    </>
  );
};

export default QuestionForm;
